import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save } from 'lucide-react';
import toast from 'react-hot-toast';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (taskData: {
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    due_date: string;
    order_id?: string;
    client_id?: string;
  }) => Promise<void>;
  orders: Array<{ id: string; title: string; client?: { id: string; name: string } }>;
  clients: Array<{ id: string; name: string }>;
  defaultDate?: string;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  orders,
  clients,
  defaultDate,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    due_date: defaultDate || '',
    order_id: '',
    client_id: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter orders by selected client
  const filteredOrders = useMemo(() => {
    if (!formData.client_id) return orders;
    return orders.filter(order => order.client?.id === formData.client_id);
  }, [orders, formData.client_id]);

  // Reset order_id when client changes
  useEffect(() => {
    if (formData.client_id && formData.order_id) {
      const selectedOrder = orders.find(o => o.id === formData.order_id);
      if (selectedOrder?.client?.id !== formData.client_id) {
        setFormData(prev => ({ ...prev, order_id: '' }));
      }
    }
  }, [formData.client_id, orders, formData.order_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreate({
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        due_date: formData.due_date || undefined,
        order_id: formData.order_id || undefined,
        client_id: formData.client_id || undefined,
      });
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: defaultDate || '',
        order_id: '',
        client_id: '',
      });
      onClose();
      // Don't show toast here - createTask in useTasks already shows it
    } catch (error) {
      // Don't show toast here - createTask in useTasks already shows it
      console.error('Error in AddTaskModal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[rgb(var(--card))] rounded-2xl border border-[rgb(var(--border))] w-full max-w-md shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[rgb(var(--border))]">
                <h2 className="text-xl font-semibold text-[rgb(var(--fg))]">Add New Task</h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-[rgb(var(--card))] rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-[rgb(var(--muted))]" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Enter task title..."
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                    placeholder="Enter task description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                      className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Client (Optional)
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value, order_id: '' })}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">No Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[rgb(var(--muted))] mb-2">
                    Project (Optional)
                  </label>
                  <select
                    value={formData.order_id}
                    onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                    className="w-full px-4 py-2 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">
                      {formData.client_id && filteredOrders.length === 0
                        ? 'No projects for this client' 
                        : 'No Project'}
                    </option>
                    {filteredOrders.map(order => (
                      <option key={order.id} value={order.id}>{order.title}</option>
                    ))}
                  </select>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-[#9c68f2] text-[rgb(var(--fg))] rounded-lg hover:bg-[#8a5cf0] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {isSubmitting ? 'Creating...' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddTaskModal;

